<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import {
    BrainCircuit,
    Download,
    FileText,
    Plus,
    Settings2,
    Sparkles,
    TableProperties,
} from 'lucide-vue-next';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { ensure as ensureGrid } from '@/actions/App/Http/Controllers/ExtractionGridController';
import { download as fileDownload } from '@/actions/App/Http/Controllers/FileController';
import EmptyState from '@/components/shared/EmptyState.vue';
import RunProgressTracker from '@/components/shared/RunProgressTracker.vue';
import { Button } from '@/components/ui/button';
import { useProjectChannel } from '@/composables/useProjectChannel';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import ColumnsManagerSheet from '@/pages/Data/ColumnsManagerSheet.vue';
import EditableCell from '@/pages/Data/EditableCell.vue';
import * as runsRoutes from '@/routes/runs';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Column {
    id: string;
    name: string;
    type: string;
    position: number;
    config: Record<string, unknown> | null;
}

interface Cell {
    id: string;
    row_id: string;
    column_id: string;
    value: { text?: string } | null;
    status: string | null;
    ai_generated: boolean;
}

interface PaperPdf {
    id: string;
    original_filename: string;
}

interface Row {
    id: string;
    paper: {
        id: string;
        title: string;
        authors: string | string[];
        year: number | null;
        pdfs: PaperPdf[];
    };
    cells: Cell[];
}

interface Grid {
    id: string;
    columns: Column[];
}

interface Props {
    project: { id: string; name: string };
    grid: Grid | null;
    rows: Row[];
    active_run_id?: string | null;
}

const props = defineProps<Props>();

defineOptions({
    layout: (p: Props) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                { title: p.project.name, href: `/projects/${p.project.id}` },
                { title: 'Extraction', href: '#' },
            ],
        },
    ],
});

// ─── PDF Viewer ───────────────────────────────────────────────────────────────

const PdfViewer = defineAsyncComponent(
    () => import('@/components/shared/PdfViewer.vue'),
);

const viewerPdfs = ref<PaperPdf[]>([]);
const isViewerOpen = ref(false);

function openPdfViewer(row: Row) {
    viewerPdfs.value = row.paper.pdfs;
    isViewerOpen.value = true;
}

// ─── Local state (reactive, synced from props on Inertia reload) ───────────────

const sheetOpen = ref(false);
const creatingGrid = ref(false);
const localColumns = ref<Column[]>(props.grid?.columns ?? []);
const localRows = ref<Row[]>(props.rows);

// ─── Extraction run tracking ──────────────────────────────────────────────────

const extractionRunId = ref<string | null>(props.active_run_id ?? null);

onMounted(() => {
    if (props.active_run_id) {
        extractionRunId.value = props.active_run_id;
    }
});

watch(
    () => props.active_run_id,
    (activeRunId) => {
        if (activeRunId) {
            extractionRunId.value = activeRunId;
        }
    },
);

function onExtractionCompleted() {
    router.reload({ only: ['rows'] });
    setTimeout(() => {
        extractionRunId.value = null;
    }, 2000);
}

function onExtractionFailed() {
    toast.error("L'extraction IA a échoué.");
    extractionRunId.value = null;
}

function onExtractionCancelled() {
    extractionRunId.value = null;
    router.reload({ only: ['rows'] });
}

watch(
    () => props.grid?.columns,
    (cols) => {
        localColumns.value = cols ?? [];
    },
    { deep: true },
);

watch(
    () => props.rows,
    (rows) => {
        localRows.value = rows;
    },
    { deep: true },
);

// ─── Realtime updates ─────────────────────────────────────────────────────────

const { projectEvents } = useProjectChannel(props.project.id);

watch(projectEvents, (events) => {
    const latest = events.at(-1);

    if (!latest) {
        return;
    }

    const runType = (latest.payload.data as { type?: string } | undefined)
        ?.type;

    if (latest.type === 'project.extraction_updated') {
        router.reload({ only: ['rows'] });
    }

    if (
        latest.type === 'project.run_completed' &&
        (runType === 'columns' || runType === 'extraction')
    ) {
        router.reload({ only: ['grid', 'rows'] });
    }
});

// ─── Grid creation ─────────────────────────────────────────────────────────────

function createGrid() {
    creatingGrid.value = true;

    router.post(
        ensureGrid.url(props.project.id),
        {},
        {
            preserveScroll: true,
            only: ['grid', 'rows'],
            onFinish: () => {
                creatingGrid.value = false;
            },
        },
    );
}

// ─── Columns reload ────────────────────────────────────────────────────────────

const onColumnsChanged = () => {
    router.reload({ only: ['grid', 'rows'] });
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toast = useToast();

function getCsrfToken(): string {
    return (
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
            ?.content ?? ''
    );
}

async function postRun(type: string): Promise<Response> {
    return fetch(runsRoutes.store().url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCsrfToken(),
            Accept: 'application/json',
        },
        body: JSON.stringify({ project_id: props.project.id, type }),
    });
}

// ─── Propose columns ──────────────────────────────────────────────────────────

const proposalLoading = ref(false);

async function proposeColumns() {
    proposalLoading.value = true;

    try {
        const res = await postRun('columns');

        if (res.status === 409) {
            toast.info(
                'Une suggestion de colonnes est déjà en cours pour ce projet.',
            );

            return;
        }

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(
                (err as { message?: string }).message ??
                    'Erreur lors de la suggestion de colonnes.',
            );

            return;
        }

        toast.success("L'IA analyse vos articles pour suggérer des colonnes…");
    } catch {
        toast.error('Erreur réseau lors de la suggestion de colonnes.');
    } finally {
        proposalLoading.value = false;
    }
}

// ─── Extraction run trigger ─────────────────────────────────────────────────

const extractionLoading = ref(false);

async function startExtraction() {
    extractionLoading.value = true;

    try {
        const res = await postRun('extraction');

        if (res.status === 409) {
            const data = (await res.json().catch(() => ({}))) as {
                message?: string;
                active_run_id?: string;
            };
            toast.info('Une extraction est déjà en cours pour ce projet.');

            if (data.active_run_id) {
                extractionRunId.value = data.active_run_id;
            }

            return;
        }

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast.error(
                (err as { message?: string }).message ??
                    "Erreur lors du démarrage de l'extraction.",
            );

            return;
        }

        toast.success(
            'Extraction IA démarrée. Les résultats arriveront progressivement.',
        );
        const run = (await res.json()) as { id: string };
        extractionRunId.value = run.id;
        router.reload({ only: ['rows'] });
    } catch {
        toast.error("Erreur réseau lors du démarrage de l'extraction.");
    } finally {
        extractionLoading.value = false;
    }
}

// ─── Cell helpers ──────────────────────────────────────────────────────────────

function cellMeta(row: Row, columnId: string): Cell | null {
    return row.cells.find((c) => c.column_id === columnId) ?? null;
}

function cellValue(row: Row, columnId: string): { text?: string } | null {
    return cellMeta(row, columnId)?.value ?? null;
}

function onCellUpdated(
    rowId: string,
    columnId: string,
    value: { text?: string } | null,
) {
    const row = localRows.value.find((r) => r.id === rowId);

    if (!row) {
        return;
    }

    const cell = row.cells.find((c) => c.column_id === columnId);

    if (cell) {
        cell.value = value;
        cell.ai_generated = false;
        cell.status = null;
    } else {
        row.cells.push({
            id: '',
            row_id: rowId,
            column_id: columnId,
            value,
            status: null,
            ai_generated: false,
        });
    }
}

function onCellStatusChanged(
    rowId: string,
    columnId: string,
    status: string | null,
) {
    const row = localRows.value.find((r) => r.id === rowId);

    if (!row) {
        return;
    }

    const cell = row.cells.find((c) => c.column_id === columnId);

    if (cell) {
        cell.status = status;
    }
}

// ─── Data legend stats ───────────────────────────────────────────────────────

const allCells = computed(() => localRows.value.flatMap((r) => r.cells));
const pendingCells = computed(
    () => allCells.value.filter((c) => c.status === 'extracted').length,
);
const validatedCells = computed(
    () => allCells.value.filter((c) => c.status === 'validated').length,
);
const rejectedCells = computed(
    () => allCells.value.filter((c) => c.status === 'rejected').length,
);
const processedArticles = computed(
    () => localRows.value.filter((r) => r.cells.some((c) => c.value)).length,
);

const hasNoExtractedData = computed(
    () =>
        localRows.value.length > 0 &&
        localColumns.value.length > 0 &&
        allCells.value.every((c) => !c.value),
);

const cellCountByColumn = computed(() => {
    const counts: Record<string, number> = {};

    for (const row of localRows.value) {
        for (const cell of row.cells) {
            if (cell.value !== null) {
                counts[cell.column_id] = (counts[cell.column_id] ?? 0) + 1;
            }
        }
    }

    return counts;
});

// ─── Type labels ───────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
    text: 'Texte',
    number: 'Nombre',
    boolean: 'Booléen',
    date: 'Date',
    select: 'Liste',
    multi_select: 'Multi-liste',
};

// ─── Column resize ─────────────────────────────────────────────────────────────

const articleWidth = ref(300);
const colWidths = ref<Record<string, number>>({});

watch(
    localColumns,
    (cols) => {
        const next: Record<string, number> = {};

        for (const col of cols) {
            next[col.id] = colWidths.value[col.id] ?? 220;
        }

        colWidths.value = next;
    },
    { immediate: true },
);

const totalTableWidth = computed(
    () =>
        articleWidth.value +
        localColumns.value.reduce(
            (s, c) => s + (colWidths.value[c.id] ?? 220),
            0,
        ),
);

let _resizingCol: string | 'article' | null = null;
let _resizeStartX = 0;
let _resizeStartWidth = 0;

function startResize(e: MouseEvent, colId: string | 'article') {
    e.preventDefault();
    _resizingCol = colId;
    _resizeStartX = e.clientX;
    _resizeStartWidth =
        colId === 'article'
            ? articleWidth.value
            : (colWidths.value[colId] ?? 220);
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', stopResize);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
}

function onResizeMove(e: MouseEvent) {
    if (!_resizingCol) {
        return;
    }

    const w = Math.max(120, _resizeStartWidth + e.clientX - _resizeStartX);

    if (_resizingCol === 'article') {
        articleWidth.value = w;
    } else {
        colWidths.value[_resizingCol] = w;
    }
}

function stopResize() {
    _resizingCol = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', stopResize);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
}
</script>

<template>
    <div class="flex min-h-0 flex-1 flex-col">
        <!-- Page header -->
        <div class="shrink-0 border-b border-border/40 px-6 py-5">
            <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div
                        class="text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Synthèse
                    </div>
                    <h1 class="mt-1 text-2xl font-semibold tracking-tight">
                        Données extraites des articles
                    </h1>
                    <p class="mt-1 text-sm text-muted-foreground">
                        Tableau d'extraction — chaque cellule peut être proposée
                        par l'assistant, validée par vous ou refusée.
                    </p>
                </div>
                <div
                    v-if="grid && localColumns.length > 0"
                    class="flex items-center gap-2"
                    data-tour="generate-grid-btn"
                >
                    <Button
                        size="sm"
                        :disabled="extractionLoading || !!extractionRunId"
                        @click="startExtraction"
                    >
                        <BrainCircuit class="mr-1.5 h-3.5 w-3.5" />
                        {{
                            extractionLoading
                                ? 'Démarrage…'
                                : extractionRunId
                                  ? 'Extraction en cours…'
                                  : "Lancer l'extraction IA"
                        }}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        @click="sheetOpen = true"
                    >
                        <Settings2 class="mr-1.5 h-3.5 w-3.5" />
                        Gérer les colonnes
                    </Button>
                    <Button size="sm" variant="outline">
                        <Download class="mr-1.5 h-3.5 w-3.5" />
                        Exporter
                    </Button>
                    <Button size="sm" @click="sheetOpen = true">
                        <Plus class="mr-1.5 h-3.5 w-3.5" />
                        Colonne
                    </Button>
                </div>
            </div>

            <!-- Data legend -->
            <div
                v-if="grid && localRows.length > 0"
                class="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground"
            >
                <span class="flex items-center gap-1.5">
                    <span class="h-2 w-2 rounded-full bg-violet-400"></span>
                    Cellule proposée par l'assistant
                    <span
                        v-if="pendingCells > 0"
                        class="font-semibold text-foreground"
                        >({{ pendingCells }})</span
                    >
                </span>
                <span class="flex items-center gap-1.5">
                    <span class="h-2 w-2 rounded-full bg-green-500"></span>
                    Validée
                    <span
                        v-if="validatedCells > 0"
                        class="font-semibold text-foreground"
                        >({{ validatedCells }})</span
                    >
                </span>
                <span class="flex items-center gap-1.5">
                    <span class="h-2 w-2 rounded-full bg-red-400"></span>
                    Refusée
                    <span
                        v-if="rejectedCells > 0"
                        class="font-semibold text-foreground"
                        >({{ rejectedCells }})</span
                    >
                </span>
                <span class="flex items-center gap-1.5">
                    <span
                        class="h-2 w-2 rounded-full border border-border bg-muted"
                    ></span>
                    Vide
                </span>
                <span class="ml-auto text-xs">
                    {{ pendingCells }} cellule{{
                        pendingCells !== 1 ? 's' : ''
                    }}
                    à valider · {{ processedArticles }} article{{
                        processedArticles !== 1 ? 's' : ''
                    }}
                    traité{{ processedArticles !== 1 ? 's' : '' }} sur
                    {{ localRows.length }}
                </span>
            </div>
        </div>

        <!-- Extraction progress tracker -->
        <RunProgressTracker
            v-if="extractionRunId"
            :run-id="extractionRunId"
            feature-label="Extraction IA en cours…"
            @completed="onExtractionCompleted"
            @failed="onExtractionFailed"
            @cancelled="onExtractionCancelled"
        />

        <!-- Empty state — no grid yet -->
        <EmptyState
            v-if="!grid"
            :icon="TableProperties"
            title="Aucune grille d'extraction"
            description="Laissez l'IA analyser vos articles pour proposer des colonnes pertinentes, ou créez une grille vide à remplir manuellement."
            class="m-6"
        >
            <div class="flex items-center gap-2">
                <Button :disabled="proposalLoading" @click="proposeColumns">
                    <Sparkles class="mr-1.5 h-4 w-4" />
                    {{
                        proposalLoading
                            ? 'Analyse en cours…'
                            : 'Générer la grille (IA)'
                    }}
                </Button>
                <Button
                    variant="outline"
                    :disabled="creatingGrid"
                    @click="createGrid"
                >
                    <Plus class="mr-1.5 h-4 w-4" />
                    {{ creatingGrid ? 'Création…' : 'Créer la grille' }}
                </Button>
            </div>
        </EmptyState>

        <template v-else>
            <!-- Empty state — grid exists but no columns yet -->
            <EmptyState
                v-if="localColumns.length === 0"
                :icon="Sparkles"
                title="Aucune colonne définie"
                description="Laissez l'IA analyser vos articles pour suggérer des colonnes pertinentes, ou ajoutez-les manuellement."
                class="m-6"
            >
                <div class="flex items-center gap-2">
                    <Button :disabled="proposalLoading" @click="proposeColumns">
                        <Sparkles class="mr-1.5 h-4 w-4" />
                        {{
                            proposalLoading
                                ? 'Analyse en cours…'
                                : 'Proposer des colonnes (IA)'
                        }}
                    </Button>
                    <Button variant="outline" @click="sheetOpen = true">
                        <Plus class="mr-1.5 h-4 w-4" />
                        Ajouter manuellement
                    </Button>
                </div>
            </EmptyState>

            <template v-else>
                <!-- Empty rows state -->
                <EmptyState
                    v-if="localRows.length === 0"
                    title="Aucun article retenu"
                    description="Marquez des articles comme retenus pour les voir apparaître ici."
                    class="m-6"
                />

                <template v-else>
                    <!-- Banner: rows exist but no extraction done yet -->
                    <div
                        v-if="hasNoExtractedData && !extractionRunId"
                        class="mx-6 mt-4 flex items-center gap-4 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3"
                    >
                        <BrainCircuit class="h-5 w-5 shrink-0 text-brand" />
                        <div class="min-w-0 flex-1">
                            <p class="text-sm font-medium">
                                Aucune donnée extraite pour le moment
                            </p>
                            <p class="text-xs text-muted-foreground">
                                Lancez l'extraction IA pour remplir
                                automatiquement les cellules depuis les PDFs de
                                vos articles.
                            </p>
                        </div>
                        <Button
                            size="sm"
                            class="shrink-0 bg-brand hover:bg-brand/90"
                            :disabled="extractionLoading"
                            @click="startExtraction"
                        >
                            <BrainCircuit class="mr-1.5 h-3.5 w-3.5" />
                            Lancer l'extraction IA
                        </Button>
                    </div>

                    <!-- Scrollable data table -->
                    <div
                        class="min-h-0 flex-1 overflow-auto"
                        data-tour="extraction-grid"
                    >
                        <table
                            class="table-fixed border-separate border-spacing-0 text-sm"
                            :style="{ width: totalTableWidth + 'px' }"
                        >
                            <colgroup>
                                <col :style="{ width: articleWidth + 'px' }" />
                                <col
                                    v-for="col in localColumns"
                                    :key="'cg-' + col.id"
                                    :style="{
                                        width:
                                            (colWidths[col.id] ?? 220) + 'px',
                                    }"
                                />
                            </colgroup>

                            <thead class="sticky top-0 z-20">
                                <tr>
                                    <!-- Fixed article column header -->
                                    <th
                                        class="group/th relative sticky left-0 z-30 border-r border-b border-border/40 bg-muted/90 px-4 py-3 text-left font-medium text-muted-foreground backdrop-blur-sm"
                                    >
                                        Article
                                        <div
                                            class="absolute top-0 right-0 z-10 flex h-full w-2 cursor-col-resize items-center justify-center opacity-0 transition-opacity group-hover/th:opacity-100"
                                            @mousedown.stop="
                                                startResize($event, 'article')
                                            "
                                        >
                                            <div
                                                class="h-4 w-px rounded-full bg-border"
                                            ></div>
                                        </div>
                                    </th>

                                    <!-- Dynamic column headers -->
                                    <th
                                        v-for="col in localColumns"
                                        :key="col.id"
                                        class="group/th relative border-r border-b border-border/40 bg-muted/90 px-3 py-3 text-left font-medium text-muted-foreground backdrop-blur-sm last:border-r-0"
                                    >
                                        <div
                                            class="flex items-center gap-2 overflow-hidden pr-2"
                                        >
                                            <span class="truncate">{{
                                                col.name
                                            }}</span>
                                            <span
                                                class="shrink-0 rounded bg-background/60 px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground/70 ring-1 ring-border/40"
                                            >
                                                {{
                                                    TYPE_LABELS[col.type] ??
                                                    col.type
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            class="absolute top-0 right-0 z-10 flex h-full w-2 cursor-col-resize items-center justify-center opacity-0 transition-opacity group-hover/th:opacity-100"
                                            @mousedown.stop="
                                                startResize($event, col.id)
                                            "
                                        >
                                            <div
                                                class="h-4 w-px rounded-full bg-border"
                                            ></div>
                                        </div>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr
                                    v-for="row in localRows"
                                    :key="row.id"
                                    class="group"
                                >
                                    <!-- Sticky article info -->
                                    <td
                                        class="sticky left-0 z-10 overflow-hidden border-r border-b border-border/20 bg-card px-4 py-3 shadow-[2px_0_6px_-2px_rgba(0,0,0,0.06)] transition-colors group-hover:bg-muted/30"
                                    >
                                        <p
                                            class="truncate leading-tight font-medium"
                                        >
                                            {{ row.paper.title }}
                                        </p>
                                        <p
                                            class="mt-0.5 truncate text-xs text-muted-foreground"
                                        >
                                            {{
                                                Array.isArray(row.paper.authors)
                                                    ? row.paper.authors.join(
                                                          ', ',
                                                      )
                                                    : row.paper.authors
                                            }}
                                            · {{ row.paper.year ?? '?' }}
                                        </p>
                                        <button
                                            v-if="row.paper.pdfs.length > 0"
                                            type="button"
                                            class="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-brand transition-colors hover:bg-brand/10"
                                            @click.stop="openPdfViewer(row)"
                                        >
                                            <FileText class="h-3 w-3" />
                                            {{
                                                row.paper.pdfs.length > 1
                                                    ? `${row.paper.pdfs.length} PDF`
                                                    : 'PDF'
                                            }}
                                        </button>
                                    </td>

                                    <!-- Editable cells -->
                                    <td
                                        v-for="col in localColumns"
                                        :key="col.id"
                                        class="overflow-hidden border-r border-b border-border/20 p-0 last:border-r-0"
                                        data-tour="extraction-cells"
                                    >
                                        <EditableCell
                                            :row-id="row.id"
                                            :column-id="col.id"
                                            :grid-id="grid.id"
                                            :type="col.type"
                                            :config="col.config"
                                            :initial-value="
                                                cellValue(row, col.id)
                                            "
                                            :cell-id="
                                                cellMeta(row, col.id)?.id ??
                                                null
                                            "
                                            :status="
                                                cellMeta(row, col.id)?.status ??
                                                null
                                            "
                                            :ai-generated="
                                                cellMeta(row, col.id)
                                                    ?.ai_generated ?? false
                                            "
                                            @update:value="
                                                onCellUpdated(
                                                    row.id,
                                                    col.id,
                                                    $event,
                                                )
                                            "
                                            @update:status="
                                                onCellStatusChanged(
                                                    row.id,
                                                    col.id,
                                                    $event,
                                                )
                                            "
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
            </template>
        </template>

        <!-- Columns management panel -->
        <ColumnsManagerSheet
            v-if="grid"
            v-model:open="sheetOpen"
            :grid="grid"
            :columns="localColumns"
            :cell-counts="cellCountByColumn"
            @columns-changed="onColumnsChanged"
        />

        <!-- PDF Viewer -->
        <PdfViewer
            v-if="viewerPdfs.length > 0"
            :open="isViewerOpen"
            :pdfs="viewerPdfs"
            :resolve-url="
                (fileId: string) =>
                    fileDownload.url({ project: project.id, file: fileId })
            "
            @update:open="isViewerOpen = $event"
        />
    </div>
</template>
